import React, { useCallback } from 'react';
import { Bubble, BubbleProps } from 'tea-component';
import { TipsMapType } from '.';
import { getLocale } from 'umi';
import HoverSvgIcon from '../HoverSvgIcon';
import type { BubbleLanguageType, LocaleType } from '../../types/common';
import './index.scss';
import SvgIcon from '@/pages/home/components/SvgIcon';
import classnames from 'classnames';
import useLanguage from '@op/hooks/useLanguage';

const locale2Language: {
  [key in LocaleType]: BubbleLanguageType;
} = {
  'en-US': 'en',
  'zh-CN': 'zh',
};

export interface BubbleCommonType extends BubbleProps {
  tipsMap: TipsMapType | null | undefined;
  children?: any;
  showIcon?: boolean;
  frontIcon?: boolean;
  iconStyle?: React.CSSProperties;
  childStyle?: React.CSSProperties;
  customIcon?: any;
  hasCloseIcon?: boolean;
  handleCloseIconClick?: Function;
  selected?: boolean;
  extraContent?: React.ReactNode;
  childClassName?: string;
}

/**
 * 共用气泡组件
 * @param tipsMap    提示文案列表，可以为string或者元素或者number，number代表空几行
 * @param children    Bubble组件外部显示内容
 * @param showIcon    是否显示图标，默认显示
 * @param frontIcon   是否图表前置，如果不显示图表直接取消
 * @param otherProps  其他参数，设置Bubble，会覆盖当前默认参数
 * @returns
 */
export default function BubbleCommon({
  tipsMap,
  children,
  showIcon = true,
  frontIcon = false,
  customIcon,
  iconStyle = { marginLeft: 5 },
  hasCloseIcon = false,
  handleCloseIconClick = (e: Event) => {},
  style = {},
  childStyle = {},
  selected,
  extraContent,
  childClassName,
  ...otherProps
}: BubbleCommonType) {
  /**
   * 获取Bubble的提示信息
   */
  const { formatMessage } = useLanguage();

  const getListInfo = useCallback(() => {
    const locale: LocaleType = getLocale();
    const { sort, space, ...languageMap } = tipsMap || {};
    if (!languageMap[locale2Language[locale]]) return null;
    return (
      <div
        className={hasCloseIcon ? 'bubble-common-content-close-wrapper' : ''}
        onClick={(e) => handleCloseIconClick?.(e)}
        style={{
          cursor: hasCloseIcon ? 'pointer' : 'default',
        }}
      >
        {languageMap[locale2Language[locale]]}
        {hasCloseIcon && (
          <i className="dbi-icon-close">
            <SvgIcon type="clonse" />
          </i>
        )}
        {extraContent ?? null}
      </div>
    );
  }, [tipsMap, formatMessage, extraContent]);

  /**
   * 获取Bubble的显示信息
   */
  const getChildrenInfo = useCallback(() => {
    if (!showIcon) {
      return children;
    }

    const icon = customIcon || (
      <HoverSvgIcon
        className="db-tipcontext-icon"
        style={{
          ...iconStyle,
        }}
        selected={selected}
      ></HoverSvgIcon>
    );

    return (
      <>
        {frontIcon && icon}
        {children}
        {!frontIcon && icon}
      </>
    );
  }, [frontIcon, children, showIcon, customIcon, selected]);

  return (
    <Bubble
      dark={true}
      className="bubble-common-wrapper"
      // width: '450px', 这里的不能写死，不然中英文翻译太宽了，走查先注释了
      style={{ maxWidth: '450px', ...style }}
      placement="auto"
      closeOnScroll
      // trigger={"click"}
      content={getListInfo()}
      {...otherProps}
    >
      <span
        style={childStyle}
        className={classnames(
          'bubble-common-child-wrapper',
          getListInfo() ? '' : 'no-pointer',
          childClassName,
        )}
      >
        {getChildrenInfo()}
      </span>
    </Bubble>
  );
}
